import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import MainLayout from "../../../components/layout/MainLayout";
import PROSAssessmentForm from "./PROSAssessmentForm";
import {
    createProsAssessment,
    clearError,
} from "../../../store/slices/prosAssessmentsSlice";
import { fetchPatient } from "../../../store/slices/patientsSlice";

const AddPROSAssessment = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const { isLoading, error } = useSelector((state) => state.prosAssessments);

    // === Initial Form Data ===
    const initialFormData = {
        patientId, // initialize
        caseId,    // initialize
        clientName: "",
        effectiveDate: "",
        part1: {
            tellMeAboutYou: { text: "", wantToWork: "" },
            dailyRoutine: { text: "", wantToWork: "" },
            employment: { text: "", wantToWork: "" },
            learning: { text: "", wantToWork: "" },
            housing: { text: "", wantToWork: "" },
            healthyLifestyle: { text: "", wantToWork: "" },
            relationships: { text: "", wantToWork: "" },
            parenting: { text: "", wantToWork: "" },
            activities: { text: "", wantToWork: "" },
        },
        part2: {
            goals: [{ id: Date.now(), text: "" }],
            exploringGoal: "",
            imaginingChange: "",
            skillsSupports: "",
            overcomingBarriers: "",
            journeySupport: "",
        },
        part3: {
            practitionerSummary: "",
            practitionerRecommendations: "",
            checkedItems: {
                lifeRoleDevelopment: false,
                lifeRoleRefinement: false,
                lifeRoleAcquisition: false,
                lifeRoleRetention: false,
            },
        },
    };

    const [formData, setFormData] = React.useState(initialFormData);
    const [errors, setErrors] = React.useState({});

    // === Clear error on mount ===
    useEffect(() => {
        dispatch(clearError());
    }, [dispatch]);

    // ⭐ ADD THIS HOOK BLOCK HERE ⭐
    useEffect(() => {
        if (patientId) {
            dispatch(fetchPatient(patientId))
                .unwrap()
                .then((patient) => {
                    if (patient) {
                        setFormData((prev) => ({
                            ...prev,
                            clientName: `${patient.first_name || ""} ${patient.last_name || ""}`.trim(),
                        }));
                    }
                })
                .catch((err) => console.error("Failed to prefill patient name for PROS form:", err));
        }
    }, [dispatch, patientId]);

    // === Validation ===
    const validate = () => {
        const newErrors = {};
        if (!formData.clientName) newErrors.clientName = "Client Name is required.";
        if (!formData.effectiveDate) newErrors.effectiveDate = "Date is required.";
        if (formData.part2.goals.some((g) => !g.text))
            newErrors.goals = "All goals must have text.";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // === Payload Formatter (camelCase → snake_case, "" → null) ===
    const formatPayload = (data) => {
        const toNull = (val) => (val === "" ? null : val);

        return {
            patient_id: toNull(data.patientId),
            case_id: toNull(data.caseId),
            client_name: data.clientName,
            effective_date: data.effectiveDate,

            // === Part 1 ===
            part1: {
                tellMeAboutYou: {
                    text: toNull(data.part1.tellMeAboutYou.text),
                    want_to_work: toNull(data.part1.tellMeAboutYou.wantToWork),
                },
                dailyRoutine: {
                    text: toNull(data.part1.dailyRoutine.text),
                    want_to_work: toNull(data.part1.dailyRoutine.wantToWork),
                },
                employment: {
                    text: toNull(data.part1.employment.text),
                    want_to_work: toNull(data.part1.employment.wantToWork),
                },
                learning: {
                    text: toNull(data.part1.learning.text),
                    want_to_work: toNull(data.part1.learning.wantToWork),
                },
                housing: {
                    text: toNull(data.part1.housing.text),
                    want_to_work: toNull(data.part1.housing.wantToWork),
                },
                healthyLifestyle: {
                    text: toNull(data.part1.healthyLifestyle.text),
                    want_to_work: toNull(data.part1.healthyLifestyle.wantToWork),
                },
                relationships: {
                    text: toNull(data.part1.relationships.text),
                    want_to_work: toNull(data.part1.relationships.wantToWork),
                },
                parenting: {
                    text: toNull(data.part1.parenting.text),
                    want_to_work: toNull(data.part1.parenting.wantToWork),
                },
                activities: {
                    text: toNull(data.part1.activities.text),
                    want_to_work: toNull(data.part1.activities.wantToWork),
                },
            },

            // === Part 2 ===
            part2: {
                goals: data.part2.goals.map((g) => ({ text: g.text })),
                exploring_goal: toNull(data.part2.exploringGoal),
                imagining_change: toNull(data.part2.imaginingChange),
                skills_supports: toNull(data.part2.skillsSupports),
                overcoming_barriers: toNull(data.part2.overcomingBarriers),
                journey_support: toNull(data.part2.journeySupport),
            },

            // === Part 3 ===
            part3: {
                practitioner_summary: toNull(data.part3.practitionerSummary),
                practitioner_recommendations: toNull(data.part3.practitionerRecommendations),
                checked_items: data.part3.checkedItems,
            },
        };
    };

    // === Submit Handler ===
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) {
            Swal.fire({
                title: "Validation Error",
                html: Object.values(errors).join("<br>"),
                icon: "warning",
            });
            return;
        }

        const payload = formatPayload(formData);

        try {
            const res = await dispatch(createProsAssessment(payload)).unwrap();

            Swal.fire({
                title: "Success!",
                text: "PROS Assessment created successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });

            navigate(`/phase05/pros-assessments/edit/${res.id || res.data?.id || (res && res[0]?.id)}`, { state: { patientId: patientId || (res && res.patient_id), caseId: caseId || (res && res.case_id) } });
        } catch (err) {
            const errorMsg =
                err?.errors
                    ? Object.values(err.errors).flat().join("\n")
                    : err || "Failed to create assessment.";

            Swal.fire({
                title: "Error",
                text: errorMsg,
                icon: "error",
            });
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="card">
                    <div className="card-body">
                        {/* Backend Error */}
                        {error && (
                            <div className="alert alert-danger alert-dismissible">
                                <strong>Error:</strong> {error}
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => dispatch(clearError())}
                                />
                            </div>
                        )}

                        {/* Validation Errors */}
                        {Object.keys(errors).length > 0 && (
                            <div className="alert alert-warning">
                                {Object.values(errors).map((err, i) => (
                                    <div key={i}>• {err}</div>
                                ))}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <PROSAssessmentForm
                                formData={formData}
                                setFormData={setFormData}
                                isLoading={isLoading}
                                handleSubmit={handleSubmit}
                            />
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default AddPROSAssessment;